// mascot.ts — the ONE raster-first mascot attach shared by Splash / Menu / Result.
//
// IDENTITY UNIFICATION: the game previously shiped TWO mascots — the flat geometric wedge
// (muncher.ts) on splash/hub/result and the round raster AutoSprite muncher in-board — which read
// as two different characters. Every out-of-board mascot surface now routes through attachMascot(),
// which prefers the SAME AutoSprite idle sheets the board uses ('msPlayerIdle' / 'msRivalIdle' +
// the 'mun-*-idle' anims built in BootScene) and keeps the geometric muncher strictly as the
// headless / decode-failure fallback. Renderer-only: no Core, no data, no determinism touch.
//
// Motion contract: the idle loop + bob run only under motionAllowed(); reduced-motion holds a
// single static frame (raster) or the static geometric pose — mirrors GameScene.attachMuncherSprite.
import Phaser from 'phaser';
import { PALETTE, muncherArtReady, tex, hasTex, KEYS } from './keys';
import { motionAllowed } from './settings';
import { muncher, animateMuncher, applyCosmeticSkin, type MuncherKind, type SkinLook } from './muncher';

export interface MascotOpts {
  /** cosmetic body tint (0xRRGGBB); null/undefined = shipped art color. */
  tint?: number | null;
  /** equipped cosmetic SKIN (preferred over `tint` for cosmetics): WebGL hue-rotation so warm skins
   *  read their named color; falls back to the multiply tint. `tint` stays for non-cosmetic washes
   *  (e.g. the defeat steel dim). */
  skin?: SkinLook;
  /** add the per-sprite WebGL neon bloom (identity glow). default true; no-op on canvas/fallback. */
  bloom?: boolean;
  /** play the idle loop (raster) / chomp cycle (geometric). default true. */
  animate?: boolean;
  /** add the soft positional bob. default true (motion-gated). Callers with their own float tween pass false. */
  bob?: boolean;
  depth?: number;
}

export interface Mascot {
  /** The visual root: a Sprite when the raster sheet decoded, else the geometric Graphics fallback. */
  obj: Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics;
  /** True when the AutoSprite raster is showing (false = geometric decode-failure fallback). */
  raster: boolean;
}

const SHEET: Record<MuncherKind, string> = { player: 'msPlayerIdle', rival: 'msRivalIdle' };
const IDLE: Record<MuncherKind, string> = { player: 'mun-player-idle', rival: 'mun-rival-idle' };

/**
 * Attach the unified mascot at (x, y), sized so the art spans ~`size` px (matches the former
 * geometric footprint, so existing layouts keep their spacing). Returns the object + which lane won.
 */
export function attachMascot(
  scene: Phaser.Scene,
  kind: MuncherKind,
  x: number,
  y: number,
  size: number,
  opts: MascotOpts = {},
): Mascot {
  // S6 IDENTITY: the rival mascot on splash / menu / result is BYT3's board-token CREATURE (idle pose)
  // when its texture decoded — so the character the player duels lands from the very first screen and
  // matches the in-board token (Decision 4). Preferred over the magenta-arrowhead spritesheet + the
  // geometric triangle, both of which stay below strictly as the headless / decode-failure fallback.
  // The player mascot path is untouched (kind guard).
  if (kind === 'rival' && hasTex(KEYS.rivalPoseIdle) && scene.textures.exists(tex(KEYS.rivalPoseIdle))) {
    const spr = scene.add.sprite(x, y, tex(KEYS.rivalPoseIdle));
    // Same 1.15× footprint ratio the spritesheet path uses, so the rival keeps its existing hero
    // placement/scale; the idle pose is static (no anim), matching the reduced-motion rival read.
    spr.setScale((size * 1.15) / Math.max(spr.width || 1, spr.height || 1));
    if (opts.skin) applyCosmeticSkin(spr, opts.skin);
    else if (opts.tint != null) spr.setTint(opts.tint);
    if (opts.depth != null) spr.setDepth(opts.depth);
    if (opts.bloom !== false && scene.renderer?.type === Phaser.WEBGL) {
      try {
        spr.postFX.addBloom(opts.skin?.tint ?? PALETTE.ai, 1, 1, 1, 1.1, 4);
      } catch {
        /* FX pipeline unsupported — the flat sprite still reads fine */
      }
    }
    if (opts.bob !== false && motionAllowed()) {
      scene.tweens.add({ targets: spr, y: y - size * 0.06, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
    return { obj: spr, raster: true };
  }

  const sheet = SHEET[kind];
  if (muncherArtReady(scene) && scene.textures.exists(sheet)) {
    const spr = scene.add.sprite(x, y, sheet);
    // 1.15× the geometric footprint — the raster silhouette has transparent padding, so this lands
    // the visible body at ~the same size the Graphics muncher drew (MenuScene hero-proven ratio).
    spr.setScale((size * 1.15) / Math.max(spr.width || 1, spr.height || 1));
    if (opts.skin) applyCosmeticSkin(spr, opts.skin);
    else if (opts.tint != null) spr.setTint(opts.tint);
    if (opts.depth != null) spr.setDepth(opts.depth);
    if (opts.bloom !== false && scene.renderer?.type === Phaser.WEBGL) {
      try {
        // Identity bloom in the skin color when one is equipped (else the shipped identity color).
        spr.postFX.addBloom(opts.skin?.tint ?? (kind === 'player' ? PALETTE.player : PALETTE.ai), 1, 1, 1, 1.1, 4);
      } catch {
        /* FX pipeline unsupported — the flat sprite still reads fine */
      }
    }
    // reduceMotion invariant: hold a single static frame instead of the looping idle + bob.
    if (opts.animate !== false && motionAllowed() && scene.anims.exists(IDLE[kind])) {
      spr.play(IDLE[kind]);
    } else {
      spr.setFrame(0);
    }
    if (opts.bob !== false && motionAllowed()) {
      scene.tweens.add({ targets: spr, y: y - size * 0.06, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
    return { obj: spr, raster: true };
  }

  // Decode-failure / headless fallback ONLY: the geometric muncher keeps the surface alive. Its
  // fills use the true named color, so the skin tint applies directly (no hue trick needed).
  const gTint = opts.skin?.tint ?? opts.tint ?? undefined;
  const g = muncher(scene, kind, x, y, size, { glow: true, tint: gTint });
  if (opts.depth != null) g.setDepth(opts.depth);
  if (opts.animate !== false && motionAllowed()) {
    animateMuncher(scene, g, kind, size, { glow: true, tint: gTint });
  }
  if (opts.bob !== false && motionAllowed()) {
    scene.tweens.add({ targets: g, y: y - size * 0.06, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }
  return { obj: g, raster: false };
}

// S9 FIX-C: drawGhostSilhouette (generic two-eyed dome ghost) was REMOVED — its only consumer (the
// daily self-race card) now draws drawSpectralGhost, because the generic form read as a Pac-Man ghost
// lookalike in review (repo rule: original IP, no lookalikes). The spectral muncher below IS the
// game's ghost identity everywhere.

/** Spectral palette for a ghost-of-a-muncher: a pale violet-white, distinct from player-cyan / rival-magenta. */
export const SPECTRAL_BODY = 0xb9a7ff;
const SPECTRAL_DARK = 0x1b1740; // mouth / pupil interior (self-contained; never reads the background)

/**
 * #15 STRETCH — the ghost-of-a-MUNCHER spectral avatar. A dome + wavy-foot ghost body carrying the
 * muncher's identity — a wide chomp mouth + one big eye — in a spectral
 * violet-white with a soft double aura, so a ladder-duel ghost reads as "a spectral you", not a generic
 * blob. Drawn at the local origin (0,0). Emoji-free + original IP; renderer-only (Graphics + fixed hues).
 */
export function drawSpectralGhost(
  g: Phaser.GameObjects.Graphics,
  size: number,
  opts: { alpha?: number; glow?: boolean } = {},
): void {
  const alpha = opts.alpha ?? 1;
  const s = size;
  const RAD = Math.PI / 180;
  // Soft spectral double-aura (skipped when glow:false, e.g. a tight chip).
  if (opts.glow !== false) {
    g.fillStyle(SPECTRAL_BODY, 0.1 * alpha);
    g.fillCircle(0, 0, s * 0.62);
    g.fillStyle(SPECTRAL_BODY, 0.06 * alpha);
    g.fillCircle(0, 0, s * 0.5);
  }
  // Body: rounded dome + three wavy feet (the ghost silhouette form).
  const w = s;
  const h = s * 1.08;
  const r = w / 2;
  const left = -w / 2;
  const top = -h / 2;
  const skirt = r * 0.3;
  g.fillStyle(SPECTRAL_BODY, 0.92 * alpha);
  g.fillRoundedRect(left, top, w, h - skirt, { tl: r, tr: r, bl: 0, br: 0 });
  const feet = 3;
  const footR = w / (feet * 2);
  const footY = top + h - skirt;
  for (let i = 0; i < feet; i++) g.fillCircle(left + footR + i * footR * 2, footY, footR);
  // Wide chomp mouth opening LEFT (the muncher tell) — a dark spectral wedge cut into the face.
  const mouthCy = -h * 0.04;
  const m = 34 * RAD;
  g.fillStyle(SPECTRAL_DARK, 0.96 * alpha);
  g.slice(0, mouthCy, r * 0.74, 180 * RAD - m, 180 * RAD + m, false);
  g.fillPath();
  // One big friendly eye upper-right (away from the mouth) — the key muncher differentiator.
  const eyeR = w * 0.15;
  const eyeX = w * 0.2;
  const eyeY = top + h * 0.34;
  g.fillStyle(0xffffff, alpha);
  g.fillCircle(eyeX, eyeY, eyeR);
  g.fillStyle(SPECTRAL_DARK, alpha);
  g.fillCircle(eyeX - eyeR * 0.15, eyeY + eyeR * 0.05, eyeR * 0.5);
  g.fillStyle(0xffffff, 0.9 * alpha);
  g.fillCircle(eyeX + eyeR * 0.12, eyeY - eyeR * 0.3, eyeR * 0.18); // catch-light
}
